import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";

import type { FusionServiceMachine, Track } from "@/schemas/machine";

import { MachineServiceRow, MachineServiceToggle } from "../components";
import { MachineServiceCard } from "../machine-service-card";

interface TrackControlProps {
	readonly track: Track;
	readonly rollerTools: number[];
	readonly onToggleRollerClicked: (trackNumber: number) => void;
}

const TrackControl: React.FC<TrackControlProps> = ({ track, rollerTools, onToggleRollerClicked }) => (
	<MachineServiceRow>
		<Typography variant="h4">
			<FormattedMessage
				id="Track {trackNumber}"
				values={{ trackNumber: track.trackNumber }}
				defaultMessage="Track {trackNumber}"
			/>
		</Typography>

		<MachineServiceToggle
			label={<FormattedMessage id="Pressure Roller" defaultMessage="Pressure Roller" />}
			checked={track.trackNumber !== undefined && rollerTools.includes(track.trackNumber)}
			onChange={() => {
				if (track.trackNumber !== undefined) {
					onToggleRollerClicked(track.trackNumber);
				}
			}}
		/>
	</MachineServiceRow>
);

interface FeedRollerControlProps {
	readonly onFeedRollerForwardClicked: () => void;
}

const FeedRollerControl: React.FC<FeedRollerControlProps> = ({ onFeedRollerForwardClicked }) => (
	<MachineServiceRow>
		<Box />
		<Button variant="contained" size="large" fullWidth onClick={onFeedRollerForwardClicked}>
			<FormattedMessage id="Feed Roller Forward" defaultMessage="Feed Roller Forward" />
		</Button>
	</MachineServiceRow>
);

interface CrossHeadControlProps {
	readonly machine: FusionServiceMachine;
	readonly onToggleCutClicked: () => void;
	readonly onToggleLatchClicked: () => void;
}

const CrossHeadControl: React.FC<CrossHeadControlProps> = ({ machine, onToggleCutClicked, onToggleLatchClicked }) => (
	<MachineServiceRow>
		<Box />

		<MachineServiceToggle
			label={<FormattedMessage id="Cut" defaultMessage="Cut" />}
			checked={machine.activeCrossHeadCut === true}
			onChange={onToggleCutClicked}
		/>
		<MachineServiceToggle
			label={<FormattedMessage id="Latch" defaultMessage="Latch" />}
			checked={machine.activeCrossHeadLatch === true}
			onChange={onToggleLatchClicked}
		/>
	</MachineServiceRow>
);

interface CalibrateControlProps {
	readonly machine: FusionServiceMachine;
	readonly onCalibrateClicked: () => void;
}

const CalibrateControl: React.FC<CalibrateControlProps> = ({ machine, onCalibrateClicked }) => (
	<MachineServiceRow>
		<Box />
		<Button
			variant="contained"
			size="large"
			disabled={machine.status === "Calibrating"}
			onClick={onCalibrateClicked}
			fullWidth
		>
			<FormattedMessage id="Calibrate" defaultMessage="Calibrate" />
		</Button>
	</MachineServiceRow>
);

interface CycleControlProps {
	readonly onCycleCrossHeadToolsClicked: () => void;
	readonly onCycleTrackToolsClicked: () => void;
}

const CycleControl: React.FC<CycleControlProps> = ({ onCycleCrossHeadToolsClicked, onCycleTrackToolsClicked }) => (
	<MachineServiceRow>
		<Box />

		<Button variant="contained" size="large" onClick={onCycleCrossHeadToolsClicked} fullWidth>
			<FormattedMessage id="Cycle CrossHead Tools" defaultMessage="Cycle CrossHead Tools" />
		</Button>
		<Button variant="contained" size="large" onClick={onCycleTrackToolsClicked} fullWidth>
			<FormattedMessage id="Cycle Tracks" defaultMessage="Cycle Track Tools" />
		</Button>
	</MachineServiceRow>
);

export interface FusionServiceTemplateProps {
	readonly machine: FusionServiceMachine;
	readonly onCalibrateClicked: () => void;
	readonly onCycleCrossHeadToolsClicked: () => void;
	readonly onCycleTrackToolsClicked: () => void;
	readonly onToggleCrossHeadCutClicked: () => void;
	readonly onToggleCrossHeadLatchClicked: () => void;
	readonly onToggleTrackRollerClicked: (trackNumber: number) => void;
	readonly onFeedRollerForwardClicked: () => void;
}

export const FusionServiceTemplate: React.FC<FusionServiceTemplateProps> = ({
	machine,
	onCalibrateClicked,
	onCycleCrossHeadToolsClicked,
	onCycleTrackToolsClicked,
	onToggleCrossHeadCutClicked,
	onToggleCrossHeadLatchClicked,
	onToggleTrackRollerClicked,
	onFeedRollerForwardClicked,
}) => (
	<Stack spacing={2}>
		<MachineServiceCard title={<FormattedMessage id="Calibrate" defaultMessage="Calibrate" />}>
			<CalibrateControl machine={machine} onCalibrateClicked={onCalibrateClicked} />
		</MachineServiceCard>

		<MachineServiceCard title={<FormattedMessage id="Cycle Tools" defaultMessage="Cycle Tools" />}>
			<CycleControl
				onCycleCrossHeadToolsClicked={onCycleCrossHeadToolsClicked}
				onCycleTrackToolsClicked={onCycleTrackToolsClicked}
			/>
		</MachineServiceCard>

		<MachineServiceCard title={<FormattedMessage id="Cross Head" defaultMessage="Cross Head" />}>
			<CrossHeadControl
				machine={machine}
				onToggleCutClicked={onToggleCrossHeadCutClicked}
				onToggleLatchClicked={onToggleCrossHeadLatchClicked}
			/>
		</MachineServiceCard>

		<MachineServiceCard title={<FormattedMessage id="Feed Roller" defaultMessage="Feed Roller" />}>
			<FeedRollerControl onFeedRollerForwardClicked={onFeedRollerForwardClicked} />
		</MachineServiceCard>

		<MachineServiceCard title={<FormattedMessage id="Tracks" defaultMessage="Tracks" />}>
			<Stack spacing={2}>
				{(machine.tracks ?? []).map((track) => (
					<TrackControl
						key={track.trackNumber}
						track={track}
						rollerTools={machine.rollerTools ?? []}
						onToggleRollerClicked={onToggleTrackRollerClicked}
					/>
				))}
			</Stack>
		</MachineServiceCard>
	</Stack>
);
